import React, { useCallback, memo } from 'react';
import { Card, Icon, Tag, Typography, ButtonMenu, CircularProgress } from '@ann/web-components';
import {
  CardWrapper,
  CardTop,
  CardHeader,
  CardHeaderTitle,
  CardMeta,
  CardMetaItem,
} from './Card.styled';

const getFileIcon = (type, extension) => {
  const ext = extension?.toLowerCase() || type?.toLowerCase() || '';
  if (ext.includes('pdf')) return 'pictureAsPdf';
  if (ext.includes('doc') || ext.includes('word')) return 'description';
  if (ext.includes('xls') || ext.includes('excel') || ext.includes('sheet')) return 'tableChart';
  if (ext.includes('ppt') || ext.includes('presentation')) return 'slideshow';
  if (ext.includes('image') || ext.includes('png') || ext.includes('jpg') || ext.includes('jpeg')) return 'image';
  if (ext.includes('video')) return 'playCircle';
  if (ext.includes('audio')) return 'audioFile';
  if (ext.includes('txt') || ext.includes('text')) return 'article';
  return 'description';
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const getStatusColor = (state) => {
  switch (state?.toLowerCase()) {
    case 'ready':
    case 'completed':
    case 'active':
      return 'success';
    case 'processing':
    case 'loading':
      return 'informative';
    case 'error':
    case 'failed':
      return 'critical';
    default:
      return 'informative';
  }
};

const DocumentCard = ({ document, onClick, onAction, actionLabel = 'Delete', isRemoving = false }) => {
  const handleMenuChange = useCallback((value) => {
    if (value === 'action') {
      onAction?.(document);
    } else if (value === 'view-details') {
      onClick?.(document);
    }
  }, [document, onClick, onAction]);

  const handleCardClick = useCallback(() => {
    if (!isRemoving) {
      onClick?.(document);
    }
  }, [document, onClick, isRemoving]);

  // Handler para detener propagación del click en el menu
  const handleMenuClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const isProcessing = document.state === 'loading' || document.state === 'processing';
  const isError = document.state === 'error' || document.state === 'failed';
  const isDisabled = isProcessing || isRemoving || isError;

  return (
    <Card disabled={isDisabled}>
      <CardWrapper onClick={handleCardClick}>
        <CardTop>
          <CardHeader>
            <Icon name={getFileIcon(document.type, document.extension)} size="medium" />
            <CardHeaderTitle title={document.name}>
              <Typography 
                category="subtitle" 
                size="medium" 
                weight="bold" 
                color="title" 
                maxCharacters={30}
              >
                {document.name || 'Unnamed Document'}
              </Typography>
            </CardHeaderTitle>
            {isProcessing && (
              <Tag 
                icon="info" 
                size="small" 
                color="informative" 
                text="Processing" 
                spinner={true} 
              />
            )}
            {isError && (
              <Tag 
                icon="error" 
                size="small" 
                color="critical" 
                text={document.errorStep || 'Failed'} 
                title={document.errorMessage || 'Upload failed'}
              />
            )}
            {isRemoving ? (
              <CircularProgress size="small" />
            ) : (
              <div onClick={handleMenuClick}>
                <ButtonMenu
                  customIcon="dotsMenu"
                  disableEffects
                  iconPosition="right"
                  label=""
                  menuPosition="bottom"
                  onChange={handleMenuChange}
                  size="medium"
                  type="tertiary"
                  width="260px"
                  disabled={isError}
                >
                  <ButtonMenu.Menu>
                    <ButtonMenu.Item label={actionLabel} value="action" />
                    <ButtonMenu.Item label="View details" value="view-details" />
                  </ButtonMenu.Menu>
                </ButtonMenu>
              </div>
            )}
          </CardHeader>

          <Typography category="body" size="medium" weight="regular" color="body">
            {isError && document.errorMessage 
              ? document.errorMessage 
              : (document.description || document.type || 'Document file')
            }
          </Typography>
        </CardTop>

        <CardMeta>
          <CardMetaItem>
            <Tag icon="person" size="medium" color="informative" />
            <Typography category="body" size="small" weight="regular" color="subtitle">
              {document.owner || document.createdBy || document.uploadedBy || 'Unknown'}
            </Typography>
          </CardMetaItem>

          {document.size && (
            <CardMetaItem>
              <Tag icon="storage" size="medium" color="informative" />
              <Typography category="body" size="small" weight="regular" color="subtitle">
                {formatFileSize(document.size)}
              </Typography>
            </CardMetaItem>
          )}

          {document.state && (
            <CardMetaItem>
              <Tag icon="lock" size="medium" color={getStatusColor(document.state)} />
              <Typography category="body" size="small" weight="regular" color="subtitle">
                {document.state}
              </Typography>
            </CardMetaItem>
          )}

          <CardMetaItem>
            <Tag icon="calendarToday" size="medium" color="informative" />
            <Typography category="body" size="small" weight="regular" color="subtitle">
              {formatDate(document.created_at || document.createdAt)}
            </Typography>
          </CardMetaItem>
        </CardMeta>
      </CardWrapper>
    </Card>
  );
};

DocumentCard.displayName = 'DocumentCard';

export default memo(DocumentCard);
